import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { supabase, CLINIC_ID } from "./supabase.js";
import { friendlyError } from "./lib/format.js";
import { getActiveDivisions, loadClinicModules } from "./data.js";

const DAYS_ID = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];

function schId() {
  return "SCH-" + crypto.randomUUID().split("-")[0].toUpperCase();
}

function fmtTime(value) {
  if (!value) return "-";
  return String(value).slice(0, 5);
}

const inputStyle = {
  padding: "9px 12px",
  border: "1px solid #e8e2d5",
  borderRadius: 8,
  fontSize: 14,
  background: "#fff",
  width: "100%",
  boxSizing: "border-box",
};

function blankSlot() {
  return { day_of_week: "1", start_time: "08:00", end_time: "12:00", notes: "" };
}

export function JadwalNakesPage() {
  const [activeModules, setActiveModules] = useState({ wellness: true, estetik: true, dental: true });
  const DIVISIONS = getActiveDivisions(activeModules);
  const divisionLabel = (value) => DIVISIONS.find((row) => row.value === value)?.label || value || "Wellness / TCM";
  const divisionTone = (value) => value === "ESTETIK" ? "status-info" : value === "DENTAL" ? "status-warning" : "status-positive";
  const [staff, setStaff] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [activeDivision, setActiveDivision] = useState("ALL");
  const [quickSlots, setQuickSlots] = useState([blankSlot()]);
  const [form, setForm] = useState({
    nakes_id: "",
    day_of_week: "1",
    division: "",
    start_time: "08:00",
    end_time: "12:00",
    notes: "",
  });

  useEffect(() => {
    loadAll();
    loadClinicModules().then(setActiveModules).catch(() => {});
  }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [staffResult, scheduleResult] = await Promise.all([
        supabase
          .from("staff")
          .select("id,name,division")
          .eq("clinic_id", CLINIC_ID)
          .eq("status", "ACTIVE")
          .order("name"),
        supabase
          .from("nakes_schedules")
          .select("*")
          .eq("clinic_id", CLINIC_ID)
          .order("division")
          .order("day_of_week")
          .order("start_time"),
      ]);
      if (staffResult.error) throw staffResult.error;
      if (scheduleResult.error) throw scheduleResult.error;
      setStaff(staffResult.data || []);
      setSchedules(scheduleResult.data || []);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  const staffMap = useMemo(() => Object.fromEntries(staff.map((row) => [row.id, row])), [staff]);
  const staffForForm = useMemo(() => (
    staff.filter((row) => !row.division || row.division === "ALL" || row.division === form.division)
  ), [staff, form.division]);
  const selectedSchedules = useMemo(() => (
    schedules
      .filter((row) => row.nakes_id === form.nakes_id && (!form.division || (row.division || "WELLNESS") === form.division))
      .sort((a, b) => Number(a.day_of_week) - Number(b.day_of_week) || fmtTime(a.start_time).localeCompare(fmtTime(b.start_time)))
  ), [schedules, form.nakes_id, form.division]);
  const divisionTabs = useMemo(() => ([
    { value: "ALL", label: "Semua Poli" },
    ...DIVISIONS,
  ]), [DIVISIONS]);
  const scheduleCounts = useMemo(() => {
    const counts = Object.fromEntries(divisionTabs.map((division) => [division.value, 0]));
    for (const row of schedules) {
      const key = row.division || "WELLNESS";
      counts[key] = (counts[key] || 0) + 1;
      counts.ALL = (counts.ALL || 0) + 1;
    }
    return counts;
  }, [divisionTabs, schedules]);
  const visibleDivisions = useMemo(() => (
    activeDivision === "ALL"
      ? DIVISIONS.filter((division) => scheduleCounts[division.value] > 0)
      : DIVISIONS.filter((division) => division.value === activeDivision)
  ), [DIVISIONS, activeDivision, scheduleCounts]);

  function resetForm() {
    setForm({
      nakes_id: "",
      day_of_week: "1",
      division: "",
      start_time: "08:00",
      end_time: "12:00",
      notes: "",
    });
    setEditingId(null);
    setQuickSlots([blankSlot()]);
    setShowForm(false);
    setError("");
  }

  function openCreateForm() {
    setForm({
      nakes_id: "",
      day_of_week: "1",
      division: activeDivision !== "ALL" ? activeDivision : "",
      start_time: "08:00",
      end_time: "12:00",
      notes: "",
    });
    setEditingId(null);
    setQuickSlots([blankSlot()]);
    setShowForm(true);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function startEdit(row) {
    setEditingId(row.id);
    setShowForm(true);
    setForm({
      nakes_id: row.nakes_id,
      day_of_week: String(row.day_of_week),
      division: row.division || staffMap[row.nakes_id]?.division || "WELLNESS",
      start_time: String(row.start_time || "08:00").slice(0, 5),
      end_time: String(row.end_time || "12:00").slice(0, 5),
      notes: row.notes || "",
    });
    setQuickSlots([{
      day_of_week: String(row.day_of_week),
      start_time: String(row.start_time || "08:00").slice(0, 5),
      end_time: String(row.end_time || "12:00").slice(0, 5),
      notes: row.notes || "",
    }]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSave(event) {
    event.preventDefault();
    if (!form.nakes_id) {
      setError("Pilih nakes.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const rowsToSave = quickSlots.map((slot) => ({
        clinic_id: CLINIC_ID,
        nakes_id: form.nakes_id,
        day_of_week: Number(slot.day_of_week),
        division: form.division || staffMap[form.nakes_id]?.division || "WELLNESS",
        start_time: `${slot.start_time}:00`,
        end_time: `${slot.end_time}:00`,
        is_active: true,
        notes: slot.notes || null,
      }));
      if (rowsToSave.some((row) => row.start_time >= row.end_time)) {
        setError("Jam selesai harus lebih besar dari jam mulai.");
        return;
      }
      if (editingId) {
        const { error: updateError } = await supabase.from("nakes_schedules").update(rowsToSave[0]).eq("id", editingId);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase.from("nakes_schedules").insert(rowsToSave.map((row) => ({ ...row, id: schId() })));
        if (insertError) throw insertError;
      }
      resetForm();
      await loadAll();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  function updateSlot(index, key, value) {
    setQuickSlots((current) => current.map((slot, slotIndex) => (
      slotIndex === index ? { ...slot, [key]: value } : slot
    )));
  }

  function addSlot() {
    setQuickSlots((current) => [...current, blankSlot()]);
  }

  function removeSlot(index) {
    setQuickSlots((current) => current.length === 1 ? current : current.filter((_, slotIndex) => slotIndex !== index));
  }

  async function handleDelete(row) {
    if (!window.confirm(`Hapus jadwal ${staffMap[row.nakes_id]?.name || ""} - ${DAYS_ID[row.day_of_week]}?`)) return;
    try {
      const { error: deleteError } = await supabase.from("nakes_schedules").delete().eq("id", row.id);
      if (deleteError) throw deleteError;
      if (editingId === row.id) resetForm();
      await loadAll();
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  return (
    <div className="page-stack">
      <div className="page-heading">
        <div>
          <p>Atur jam praktik nakes per poli agar slot reservasi pasien tetap rapi.</p>
        </div>
        <button type="button" className="button button-primary jn-add-button" onClick={openCreateForm}>
          + Tambah Jadwal
        </button>
      </div>

      {showForm && (
      <article className="panel" style={{ padding: "1.5rem" }}>
        <div className="panel-head">
          <div>
            <span className="eyebrow">Master Jadwal</span>
            <h3>{editingId ? "Edit Jadwal Nakes" : "Tambah Jadwal Praktik"}</h3>
          </div>
        </div>

        {error && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{error}</div>}

        <form onSubmit={handleSave}>
          <div className="jn-row">
            <div className="jn-field">
              <label>Divisi *</label>
              <select value={form.division} onChange={(event) => setForm((current) => ({ ...current, division: event.target.value, nakes_id: "" }))} style={inputStyle} required>
                <option value="">- Pilih divisi terlebih dahulu -</option>
                {DIVISIONS.map((division) => <option key={division.value} value={division.value}>{division.label}</option>)}
              </select>
            </div>
            <div className="jn-field" style={{ flex: 2 }}>
              <label>Nakes *</label>
              <select
                value={form.nakes_id}
                disabled={!form.division}
                onChange={(event) => setForm((current) => ({ ...current, nakes_id: event.target.value }))}
                style={inputStyle}
                required
              >
                <option value="">{form.division ? "- Pilih nakes -" : "- Pilih divisi terlebih dahulu -"}</option>
                {staffForForm.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
              </select>
              {form.division && !staffForForm.length ? <small>Tidak ada nakes aktif di divisi ini.</small> : null}
            </div>
          </div>
          {form.nakes_id ? (
            <div className="jn-existing-schedule">
              <div className="jn-existing-head">
                <div>
                  <strong>Jadwal tersimpan</strong>
                  <small>Jadwal lama untuk nakes dan divisi ini tetap dipakai sebagai setting default.</small>
                </div>
                <span>{selectedSchedules.length} jadwal</span>
              </div>
              {selectedSchedules.length ? (
                <div className="jn-existing-list">
                  {selectedSchedules.map((row) => (
                    <div className="jn-existing-row" key={row.id}>
                      <span className="status status-neutral">{DAYS_ID[row.day_of_week] || row.day_of_week}</span>
                      <strong>{fmtTime(row.start_time)} - {fmtTime(row.end_time)}</strong>
                      <span className="jn-existing-note">{row.notes || "Tanpa catatan"}</span>
                      <button type="button" className="jn-link" onClick={() => startEdit(row)}>Edit</button>
                      <button type="button" className="jn-link jn-danger" onClick={() => handleDelete(row)}>Hapus</button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="jn-existing-empty">Belum ada jadwal tersimpan. Tambahkan jadwal baru di bawah.</p>
              )}
            </div>
          ) : null}
          <div className="jn-slot-heading">
            <div>
              <strong>Hari dan jam praktik</strong>
              <small>{editingId ? "Perbarui jadwal yang dipilih." : "Tambahkan beberapa hari sekaligus dengan tombol +."}</small>
            </div>
            {!editingId && <button type="button" className="button button-secondary jn-plus-button" onClick={addSlot}><Plus size={16} /> Tambah baris</button>}
          </div>
          <div className="jn-slot-list">
            {quickSlots.map((slot, index) => (
              <div className="jn-slot-row" key={`slot-${index}`}>
                <div className="jn-field">
                  <label>Hari Praktik *</label>
                  <select value={slot.day_of_week} onChange={(event) => updateSlot(index, "day_of_week", event.target.value)} style={inputStyle}>
                    {DAYS_ID.map((day, dayIndex) => <option key={dayIndex} value={dayIndex}>{day}</option>)}
                  </select>
                </div>
                <div className="jn-field">
                  <label>Jam Mulai *</label>
                  <input type="time" value={slot.start_time} onChange={(event) => updateSlot(index, "start_time", event.target.value)} style={inputStyle} required />
                </div>
                <div className="jn-field">
                  <label>Jam Selesai *</label>
                  <input type="time" value={slot.end_time} onChange={(event) => updateSlot(index, "end_time", event.target.value)} style={inputStyle} required />
                </div>
                <div className="jn-field" style={{ flex: 2 }}>
                  <label>Catatan</label>
                  <input value={slot.notes} onChange={(event) => updateSlot(index, "notes", event.target.value)} placeholder="Opsional" style={inputStyle} />
                </div>
                {!editingId && <button type="button" className="jn-remove-button" onClick={() => removeSlot(index)} disabled={quickSlots.length === 1} title="Hapus baris"><Trash2 size={17} /></button>}
              </div>
            ))}
          </div>
          <div className="jn-actions">
            <button type="submit" className="button button-primary" disabled={saving}>
              {saving ? "Menyimpan..." : editingId ? "Perbarui Jadwal" : "Tambah Jadwal"}
            </button>
            {editingId && (
              <button type="button" className="button button-quiet" onClick={resetForm}>Batal Edit</button>
            )}
          </div>
        </form>
      </article>
      )}

      <article className="panel table-panel" style={{ padding: "1.5rem" }}>
        <div className="panel-head">
          <div>
            <span className="eyebrow">Jadwal Praktik</span>
            <h3>Daftar Jadwal per Poli</h3>
          </div>
        </div>

        <div className="jn-tabs" role="tablist" aria-label="Filter poli jadwal nakes">
          {divisionTabs.map((division) => (
            <button
              key={division.value}
              type="button"
              className={`jn-tab ${activeDivision === division.value ? "jn-tab-active" : ""}`}
              onClick={() => setActiveDivision(division.value)}
            >
              {division.label}
              <span>{scheduleCounts[division.value] || 0}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="content-loading"><div className="loader" /><span>Memuat...</span></div>
        ) : visibleDivisions.length ? (
          <div className="jn-division-stack">
            {visibleDivisions.map((division) => {
              const rows = schedules
                .filter((row) => (row.division || "WELLNESS") === division.value)
                .sort((a, b) => (
                  Number(a.day_of_week) - Number(b.day_of_week)
                  || fmtTime(a.start_time).localeCompare(fmtTime(b.start_time))
                  || (staffMap[a.nakes_id]?.name || "").localeCompare(staffMap[b.nakes_id]?.name || "")
                ));
              if (!rows.length) return null;
              return (
                <section className="jn-division-section" key={division.value}>
                  <div className="jn-division-head">
                    <span className={`status ${divisionTone(division.value)}`}>{divisionLabel(division.value)}</span>
                    <strong>{rows.length} jadwal</strong>
                  </div>
                  <table className="jn-table">
                    <thead>
                      <tr>
                        <th>Nakes</th>
                        <th>Hari Praktik</th>
                        <th>Jam Mulai</th>
                        <th>Jam Selesai</th>
                        <th>Catatan</th>
                        <th>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id}>
                          <td><strong>{staffMap[row.nakes_id]?.name || row.nakes_id}</strong></td>
                          <td><span className="status status-neutral">{DAYS_ID[row.day_of_week] || row.day_of_week}</span></td>
                          <td style={{ fontFamily: "monospace" }}>{fmtTime(row.start_time)}</td>
                          <td style={{ fontFamily: "monospace" }}>{fmtTime(row.end_time)}</td>
                          <td style={{ fontSize: 13, color: "#6b6555" }}>{row.notes || "-"}</td>
                          <td>
                            <button className="jn-link" onClick={() => startEdit(row)}>Edit</button>
                            {" "}
                            <button className="jn-link jn-danger" onClick={() => handleDelete(row)}>Hapus</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              );
            })}
          </div>
        ) : (
          <div className="empty" style={{ minHeight: 120 }}>
            Belum ada jadwal untuk poli ini. Tambahkan di atas.
          </div>
        )}
      </article>

      <style>{`
        .jn-row { display:flex; gap:16px; flex-wrap:wrap; margin-bottom:14px; }
        .jn-add-button { display:inline-flex; align-items:center; gap:8px; }
        .jn-field { flex:1; min-width:140px; display:flex; flex-direction:column; gap:6px; }
        .jn-field label { font-size:12px; font-weight:600; color:#6b6555; }
        .jn-field small { color:#8b8171; font-size:12px; }
        .jn-slot-heading { align-items:center; border-top:1px solid #eee7da; display:flex; justify-content:space-between; gap:16px; margin-top:8px; padding-top:16px; }
        .jn-slot-heading strong { color:#26352f; display:block; font-size:14px; }
        .jn-slot-heading small { color:#8b8171; display:block; font-size:12px; margin-top:4px; }
        .jn-plus-button { align-items:center; display:inline-flex; gap:6px; white-space:nowrap; }
        .jn-existing-schedule { background:#f7fbf7; border:1px solid #d8ead9; border-radius:10px; margin:4px 0 16px; padding:12px; }
        .jn-existing-head { align-items:center; display:flex; justify-content:space-between; gap:12px; }
        .jn-existing-head strong { color:#24533d; display:block; font-size:14px; }
        .jn-existing-head small { color:#6b8172; display:block; font-size:12px; margin-top:4px; }
        .jn-existing-head > span { background:#e5f3e7; border-radius:999px; color:#317048; font-size:12px; font-weight:800; padding:5px 9px; white-space:nowrap; }
        .jn-existing-list { display:flex; flex-direction:column; gap:7px; margin-top:12px; }
        .jn-existing-row { align-items:center; background:#fff; border:1px solid #e5eee5; border-radius:8px; display:flex; gap:10px; min-height:38px; padding:7px 9px; }
        .jn-existing-row strong { color:#26352f; font-family:monospace; font-size:13px; }
        .jn-existing-note { color:#78877d; flex:1; font-size:12px; }
        .jn-existing-empty { color:#6b8172; font-size:13px; margin:12px 0 0; }
        .jn-slot-list { display:flex; flex-direction:column; gap:10px; margin-top:12px; }
        .jn-slot-row { align-items:flex-end; background:#fbfaf6; border:1px solid #eee7da; border-radius:10px; display:flex; gap:12px; padding:12px; }
        .jn-remove-button { align-items:center; background:#fff; border:1px solid #f0c9c3; border-radius:8px; color:#b94336; cursor:pointer; display:inline-flex; height:38px; justify-content:center; min-width:38px; }
        .jn-remove-button:disabled { cursor:not-allowed; opacity:.4; }
        .jn-actions { display:flex; gap:10px; margin-top:6px; }
        .jn-tabs { display:flex; flex-wrap:wrap; gap:8px; margin:4px 0 18px; }
        .jn-tab {
          align-items:center;
          background:#fffdf8;
          border:1px solid #d9eadc;
          border-radius:999px;
          color:#1f513f;
          cursor:pointer;
          display:inline-flex;
          font:inherit;
          font-size:13px;
          font-weight:800;
          gap:8px;
          padding:8px 12px;
        }
        .jn-tab span {
          background:#eef7ef;
          border-radius:999px;
          color:#637568;
          font-size:11px;
          min-width:22px;
          padding:2px 7px;
          text-align:center;
        }
        .jn-tab-active { background:#1fa554; border-color:#1fa554; color:#fff; }
        .jn-tab-active span { background:rgba(255,255,255,.2); color:#fff; }
        .jn-division-stack { display:flex; flex-direction:column; gap:18px; }
        .jn-division-section { background:#fffdf8; border:1px solid #e8e2d5; border-radius:12px; overflow:hidden; }
        .jn-division-head { align-items:center; background:#f7f3eb; display:flex; gap:10px; padding:12px 14px; }
        .jn-division-head strong { color:#26352f; font-size:13px; }
        .jn-table { width:100%; border-collapse:collapse; font-size:14px; }
        .jn-table th { text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:.04em; color:#7a7364; padding:10px 12px; border-bottom:1px solid #e8e2d5; }
        .jn-table td { padding:10px 12px; border-bottom:1px solid #f1ece0; vertical-align:middle; }
        .jn-link { background:none; border:none; padding:0; font-size:13px; font-weight:600; color:#2f6f4f; cursor:pointer; }
        .jn-danger { color:#c0392b; }
        @media (max-width: 760px) {
          .jn-slot-heading { align-items:flex-start; flex-direction:column; }
          .jn-existing-head { align-items:flex-start; flex-direction:column; }
          .jn-existing-row { align-items:flex-start; flex-wrap:wrap; }
          .jn-existing-note { flex-basis:100%; }
          .jn-slot-row { align-items:stretch; flex-wrap:wrap; }
          .jn-slot-row .jn-field { min-width:calc(50% - 8px); }
          .jn-remove-button { align-self:flex-end; }
        }
      `}</style>
    </div>
  );
}
